import { useState } from "react";
import { useConnect, useConnectors, type Connector } from "wagmi";
import { Button, Sheet } from "../components/ui";
import { toast } from "../state/toasts";

/** A phone or tablet, where a wallet lives in its own app rather than in the browser. */
const onPhone = () =>
  typeof navigator !== "undefined" &&
  (/android|iphone|ipad|ipod/i.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

/** Whether this browser carries a wallet at all. */
const hasInjected = () => typeof window !== "undefined" && Boolean((window as unknown as { ethereum?: unknown }).ethereum);

/** This page inside each wallet app's own browser, as each wallet's docs give the link. */
function walletLinks(): { name: string; href: string }[] {
  const here = window.location.href;
  const bare = here.replace(/^https?:\/\//, "");
  return [
    { name: "MetaMask", href: `https://link.metamask.io/dapp/${bare}` },
    { name: "Coinbase Wallet", href: `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(here)}` },
    { name: "Trust Wallet", href: `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(here)}` },
  ];
}

const declined = (error: unknown) => {
  const code = (error as { code?: unknown; cause?: { code?: unknown } })?.code ?? (error as { cause?: { code?: unknown } })?.cause?.code;
  return code === 4001 || (error as { name?: unknown })?.name === "UserRejectedRequestError";
};

/** Where to go when this browser has no wallet: the wallet apps' browsers on a phone, an extension on a desk. */
function NoWallet() {
  const [copied, setCopied] = useState(false);
  if (!onPhone()) {
    return (
      <div className="stack">
        <p className="wallet-lead">No browser wallet found. Install one, then reload this page.</p>
        <div className="wallet-list">
          <a className="wallet-row" href="https://rabby.io" target="_blank" rel="noreferrer">
            <span>Rabby</span>
            <span className="wallet-go">Get ↗</span>
          </a>
          <a className="wallet-row" href="https://metamask.io/download" target="_blank" rel="noreferrer">
            <span>MetaMask</span>
            <span className="wallet-go">Get ↗</span>
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="stack">
      <p className="wallet-lead">On a phone, playertrade opens inside your wallet app. Pick yours.</p>
      <div className="wallet-list">
        {walletLinks().map((wallet) => (
          <a key={wallet.name} className="wallet-row" href={wallet.href}>
            <span>{wallet.name}</span>
            <span className="wallet-go">Open ›</span>
          </a>
        ))}
      </div>
      <p className="wallet-fine">Another wallet? Copy this page's link and open it in your wallet's browser.</p>
      <Button
        tone="secondary"
        block
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 1_600);
          } catch {
            // clipboard blocked: the address bar still has it
          }
        }}
      >
        {copied ? "Link copied" : "Copy link"}
      </Button>
    </div>
  );
}

/** Connects a wallet: straight to it when the browser has exactly one, a sheet to pick from when it
 * has more, and a way into a wallet app when it has none. */
export function ConnectButton({ size = "md", block = false, label = "Connect" }: { size?: "sm" | "md" | "lg"; block?: boolean; label?: string }) {
  const connectors = useConnectors();
  const { mutate: connect, isPending } = useConnect();
  const [open, setOpen] = useState(false);

  const announced = connectors.filter((connector) => connector.id !== "injected");
  const choices: readonly Connector[] = announced.length > 0 ? announced : hasInjected() ? connectors : [];

  const use = (connector: Connector) => {
    setOpen(false);
    connect(
      { connector },
      {
        onError: (error) => {
          if (declined(error)) return;
          // no wallet behind the generic connector after all: offer the wallet apps instead
          if ((error as Error).name === "ProviderNotFoundError" || /provider not found/i.test(error.message)) setOpen(true);
          else toast("error", "Could not connect", error.message.split("\n")[0]);
        },
      },
    );
  };

  return (
    <div className={`connect-wrap ${block ? "connect-block" : ""}`}>
      <Button
        tone="primary"
        size={size}
        block={block}
        busy={isPending}
        onClick={() => {
          const only = choices[0];
          if (choices.length === 1 && only) use(only);
          else setOpen(true);
        }}
      >
        {isPending ? "Connecting" : label}
      </Button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Connect a wallet">
        {choices.length === 0 ? (
          <NoWallet />
        ) : (
          <div className="wallet-list">
            {choices.map((connector) => (
              <button key={connector.uid} type="button" className="wallet-row" onClick={() => use(connector)}>
                <span className="wallet-name">
                  {connector.icon ? <img src={connector.icon} alt="" width={24} height={24} /> : <span className="wallet-blank" />}
                  {connector.name}
                </span>
                <span className="wallet-go">›</span>
              </button>
            ))}
          </div>
        )}
      </Sheet>
    </div>
  );
}
